<template>
  <Layout
    class="GuidedTourView"
    :color-classes="colorClasses"
  >
    <Heading
      :class="`mb-4 last:mb-0 ${headingColorClass || ''} text-center`"
      large
      :text="headingText"
    />
    <Subheading
      :class="`mb-4 last:mb-0 ${subheadingColorClass || ''} text-center`"
      :text="guidedTourPage.subheading_text"
    />
    <div
      class="mt-8 flex justify-center flex-wrap md:flex-row-reverse"
    >
      <div
        class="relative w-10/12 md:max-w-80 min-h-40 bg-black text-white"
      >
        <transition name="fade">
          <div
            v-if="activeStop"
            :key="`active-item-${activeStop.id}`"
            class="a-cover bg-full"
            role="img"
            :aria-label="activeStop.gt_heading || activeStop.title"
            :style="{ backgroundImage: activeGTImage && `url('${activeGTImage}')` }"
          />
        </transition>
      </div>
      <ol
        v-if="list"
        :class="listClasses"
      >
        <li
          v-for="(item, i1) in list"
          :key="`gt-stop-${i1 + 1}`"
          class="mb-1 last:mb-0"
        >
          <button
            @click="activeStop = item"
            :class="[
              item.activeColorClasses,
              linkClasses,
            ]"
            :title="htmlDecode(item.gt_heading || item.title)"
             v-html="`${item.stopNo}. ${item.gt_heading || item.title}`"
          />
        </li>
      </ol>
    </div>
    <div
      v-if="activeStop"
      class="mt-8 flex-c"
    >
      <MultiBtn
        :route-to="activeStop.gtRouteTo"
        size="large"
        icon="arrow"
        :theme="startButton.button_style"
        :text="startButton.btn_text"
        :label="activeStop.gt_heading || activeStop.title"
      />
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout.vue'
import Heading from '@/components/text/Heading.vue'
import Subheading from '@/components/text/Subheading.vue'
import MultiBtn from '@/components/buttons/MultiBtn.vue'
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'GuidedTour',

  components: {
    Layout,
    Heading,
    Subheading,
    MultiBtn
  },

  data: () => ({
    activeStop: false
  }),

  computed: {
    // Store
    ...mapState([ 'options' ]),
    ...mapGetters([
      'guidedTourPage',
      'myStore'
    ]),
    gtStyles() { return this.myStore(['styles', 'guided_tour_screen']) ?? {} },
    // Background
    colorClasses() {
      const bgStyles = this.gtStyles.background ?? {}
      return bgStyles.bg_color ? `bg-${bgStyles.bg_color}` : ''
    },
    // Heading
    globalStyles() { return  },
    subheadingStyles() { return this.globalStyles.subheading ?? {} },
    headingColorClass() {
      const globalTextColor = this.myStore(['styles', 'global', 'heading', 'text_color'])
      const headingSettings = this.gtStyles.heading ?? {}
      const headingTextColor = headingSettings.text_color || globalTextColor
      return headingTextColor ? `text-${headingTextColor}` : ''
    },
    subheadingColorClass() {
      const globalTextColor = this.myStore(['styles', 'global', 'subheading', 'text_color'])
      const subheadingSettings = this.gtStyles.subheading ?? {}
      const subheadingTextColor = subheadingSettings.text_color || globalTextColor
      return subheadingTextColor ? `text-${subheadingTextColor}` : ''
    },
    // Text
    headingText() { return this.guidedTourPage.heading_text || this.guidedTourPage.title },
    // List
    listStyles() { return this.gtStyles.list ?? {} },
    linkStyles() { return this.listStyles.link_styles ?? {} },
    activeLinkStyles() { return this.linkStyles.active ?? {} },
    focusLinkStyles() { return this.linkStyles.focus ?? {} },
    hoverLinkStyles() { return this.linkStyles.hover ?? {} },
    listClasses() {
      const { listStyles } = this
      return [
        // Dimensions
        'w-10/12 md:max-w-80',
        'md:min-h-72',
        // Margins
        'md:mr-0_5',
        // Padding
        'py-2',
        // Display
        'md:flex md:justify-center md:flex-col',
        // Background
        `bg-${listStyles.bg_color ?? 'off-white'}`,
        // Color
        'color-accent-1',
        // Text
        'text-left'
      ]
    },
    list() {
      return ((this.guidedTourPage || {}).children || []).map((item, i1) => {
        const { linkStyles, activeLinkStyles } = this
        const isActive = this.activeStop === item
        item.isActive = isActive
        const styleObj = isActive ? activeLinkStyles : linkStyles
        item.activeColorClasses = `bg-${styleObj.bg_color ?? 'transparent'} text-${styleObj.text_color}`
        return item
      })
    },
    linkClasses() {
      // Data
      const { linkStyles, focusLinkStyles, hoverLinkStyles } = this
      // Return
      return [
        // Dimensions
        'w-full',
        // Padding
        'px-7 py-1_5',
        // Display
        'block',
        // BG
        `focus:bg-${focusLinkStyles.bg_color ?? 'black'} lg:hover:bg-${hoverLinkStyles.bg_color ?? 'accent-1'}`,
        // Colours
        `focus:text-${focusLinkStyles.text_color ?? 'white'} lg:hover:text-${hoverLinkStyles.text_color ?? 'white'}`,
        // Font Family
        `font-${linkStyles.font_family ?? 'body'}`,
        // Font Weight
        `font-${linkStyles.font_family ?? 'body'}-${linkStyles.font_weight ?? 'body'}`,
        // Text Transform
        linkStyles.text_transform ?? 'normal-case',
        // Interaction
        'transition-colors duration-500 ease-in-out',
        'focus:outline-none'
      ]
    },
    // Image
    activeGTImage() {
      if (!this.activeStop) { return }
      const image = this.activeStop.gt_image
      const sizes = image && image.sizes
      const url = sizes && sizes.medium_large
      const placeholderImage = this.options.placeholder_card_image
      const piSizes = placeholderImage && placeholderImage.sizes
      const piUrl = piSizes && piSizes.medium_large
      return url || piUrl
    },
    // Buttons
    startButtonSettings() { return this.gtStyles.start_button || {} },
    startButton() {
      return {
        ...this.startButtonSettings,
        ...(this.guidedTourPage.start_button || {})
      }
    },
  },

  created() {
    this.activeStop = this.list[0]
    this.$store.commit('pageMeta', { pageTitle: this.guidedTourPage.title })
  },

  methods: {
    stopClick(item) {
      this.activeStop = item
    }
  }
}
</script>

